/**
 * PM Spec V3 13/05/2026 — CompanyStampOverlay
 *
 * Compose une signature manuscrite avec le cachet entreprise par-dessus
 * (overlay bas-droite). Retourne le PNG composite en base64.
 *
 * Usage :
 *   const composite = await composeSignatureWithStamp(sigPng, stampPng, 'medium');
 */

const SIZE_MAP = { small: 0.25, medium: 0.4, large: 0.6 };

/**
 * @param {string} signatureBase64 — PNG base64 de la signature (data URL)
 * @param {string|null} stampBase64 — PNG/SVG base64 du cachet (data URL)
 * @param {string} size — 'small' | 'medium' | 'large'
 * @returns {Promise<string>} data URL du PNG composite (signature + cachet)
 */
export const composeSignatureWithStamp = (signatureBase64, stampBase64, size = 'medium') => {
  return new Promise((resolve) => {
    if (!signatureBase64) {
      resolve(null);
      return;
    }
    if (!stampBase64) {
      resolve(signatureBase64);
      return;
    }

    const sigImg = new Image();
    sigImg.crossOrigin = 'anonymous';
    sigImg.onload = () => {
      const stampImg = new Image();
      stampImg.crossOrigin = 'anonymous';
      stampImg.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = sigImg.width;
          canvas.height = sigImg.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(sigImg, 0, 0);

          const ratio = SIZE_MAP[size] || SIZE_MAP.medium;
          const stampMaxSize = Math.min(canvas.width, canvas.height) * ratio;
          const stampRatio = stampImg.width / stampImg.height;
          let sw, sh;
          if (stampImg.width > stampImg.height) {
            sw = stampMaxSize;
            sh = stampMaxSize / stampRatio;
          } else {
            sh = stampMaxSize;
            sw = stampMaxSize * stampRatio;
          }
          // Position : bas-droite avec marge
          const margin = 8;
          const sx = canvas.width - sw - margin;
          const sy = canvas.height - sh - margin;
          ctx.globalAlpha = 0.85;
          ctx.drawImage(stampImg, sx, sy, sw, sh);
          ctx.globalAlpha = 1.0;
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(signatureBase64);
        }
      };
      stampImg.onerror = () => resolve(signatureBase64);
      stampImg.src = stampBase64;
    };
    sigImg.onerror = () => resolve(null);
    sigImg.src = signatureBase64;
  });
};

export default composeSignatureWithStamp;
